import { PhoneIcon } from '@heroicons/react/24/outline'
import { href, Link } from 'react-router'
import type { OrganizationDetails } from '~/services/org-details.server'
import type { OrgStyling } from '~/services/styling.server'

const legalLinks = [
  { to: href('/terms-conditions'), label: 'Algemene voorwaarden' },
  { to: href('/shipping-returns'), label: 'Verzending & retourneren' },
  { to: href('/privacy-policy'), label: 'Privacybeleid' },
  { to: href('/cookie-instellingen'), label: 'Cookie-instellingen' }
]

export default function Footer({ organizationDetails: od, styling }: { organizationDetails: OrganizationDetails; styling: OrgStyling }) {
  return (
    <footer className="w-full bg-(--ws-footer-bg) px-6 py-16 text-sm text-(--ws-footer-text)">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 md:grid-cols-12">
        {/* Logo + Company Name */}
        <div className="flex flex-col items-center md:col-span-4 md:items-start">
          {(styling.logoFooterUrl || styling.logoUrl) && (
            <img
              src={styling.logoFooterUrl ?? styling.logoUrl!}
              alt={od.companyName}
              className="mb-4 h-auto max-h-16 w-auto max-w-32 object-contain transition-opacity duration-200 hover:opacity-80"
            />
          )}
          <div className="text-xs font-semibold tracking-[0.15em] uppercase opacity-60">{od.companyName}</div>
        </div>

        {/* Contact Information */}
        <div className="text-center md:col-span-4 md:text-left">
          <h3 className="mb-4 text-xs font-semibold tracking-[0.15em] uppercase opacity-60">Contact</h3>
          <div className="space-y-1 leading-relaxed opacity-85">
            <p className="font-medium">{od.companyName}</p>
            <p>KVK: {od.kvkNumber}</p>
            <p>BTW: {od.vatNumber}</p>
            <div className="pt-2">
              <p>
                {od.street} {od.houseNumber}
              </p>
              <p>
                {od.postalCode} {od.city}
              </p>
              <p>{od.country}</p>
            </div>
            <p className="pt-2">Contactpersoon: {od.contactPerson}</p>
          </div>
        </div>

        {/* Links */}
        <div className="text-center md:col-span-2 md:text-left">
          <h3 className="mb-4 text-xs font-semibold tracking-[0.15em] uppercase opacity-60">Informatie</h3>
          <nav className="flex flex-col gap-2 leading-relaxed">
            {legalLinks.map(link => (
              <Link key={link.to} to={link.to} className="opacity-70 transition-opacity duration-200 hover:underline hover:opacity-100">
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Call to Action */}
        <div className="flex flex-col items-center md:col-span-2 md:items-start">
          <h3 className="mb-4 text-xs font-semibold tracking-[0.15em] uppercase opacity-60">Bel ons</h3>
          <a
            href={`tel:${od.telephone}`}
            className="ease-soft shadow-card hover:shadow-card-hover inline-flex items-center gap-2 rounded-(--ws-radius-inner) bg-(--ws-btn2-bg) px-5 py-3 font-semibold whitespace-nowrap text-(--ws-btn2-text) transition-all duration-200 hover:bg-(--ws-btn2-bg-hover)">
            <PhoneIcon className="h-5 w-5" />
            {od.telephone}
          </a>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="mx-auto mt-12 max-w-6xl border-t border-(--ws-footer-border) pt-8">
        <p className="text-center text-xs opacity-60 md:text-left">
          © {new Date().getFullYear()} {od.companyName}. Alle rechten voorbehouden.
        </p>
      </div>
    </footer>
  )
}
